import type { FC, PointerEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowDown, ArrowUpRight } from 'lucide-react'

import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { clamp01 } from '@/lib/format'
import { usePrefersReducedMotion } from '@/hooks/use-media-query'
import { RollText } from '@/components/generals/roll-text'
import { ConjugationLine } from '@/components/landing/conjugation'

import {
    Chevron,
    LOGO_CHEVRON,
    LOGO_J,
    LOGO_P,
    LOGO_VIEWBOX,
    LOGO_WIDTH
} from '@/components/generals/brand'

import { conjugation, hero } from '@/constants/content'

const GOLD = '#e4d596'
const BLUE = '#3263ee'

// Focus windows of the three manifesto lines on the hero's own scroll progress.
const LINE_RANGES: Array<Array<number>> = [
    [0.36, 0.44, 0.56, 0.62],
    [0.56, 0.62, 0.74, 0.8],
    [0.74, 0.82]
]

// Scroll story in one pinned stage: the J>P mark spreads open, its chevron turns blue and grows
// until it becomes the page, and on that blue the manifesto plays out: Unë jap, Ti jep, Ne japim.
export const Hero: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const logoRef = useRef<SVGSVGElement>(null)
    const maxScale = useRef(60)
    const reduced = usePrefersReducedMotion()

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end']
    })

    const pointerX = useMotionValue(0)
    const pointerY = useMotionValue(0)
    const springX = useSpring(pointerX, { stiffness: 50, damping: 18, mass: 0.7 })
    const springY = useSpring(pointerY, { stiffness: 50, damping: 18, mass: 0.7 })

    // The chevron arm is about 22 units thick, so half of it has to outgrow the screen diagonal.
    useEffect(() => {
        const measure = () => {
            const svg = logoRef.current

            if (!svg) return

            const unit = svg.getBoundingClientRect().width / LOGO_WIDTH

            if (!unit) return

            const diagonal = Math.hypot(window.innerWidth, window.innerHeight)

            maxScale.current = Math.max(24, (diagonal / (11 * unit)) * 1.25)
        }

        measure()
        window.addEventListener('resize', measure)

        return () => window.removeEventListener('resize', measure)
    }, [])

    const chevronScale = useTransform(scrollYProgress, (progress) => {
        if (reduced) return 1

        const t = clamp01((progress - 0.02) / 0.3)

        return 1 + (maxScale.current - 1) * t * t * t
    })

    const chevronFill = useTransform(scrollYProgress, [0.1, 0.28], [GOLD, BLUE])
    const spreadLeft = useTransform(scrollYProgress, [0.02, 0.22], [0, -220])
    const spreadRight = useTransform(scrollYProgress, [0.02, 0.22], [0, 220])
    const sideOpacity = useTransform(scrollYProgress, [0.06, 0.16], [1, 0])
    const contentY = useTransform(scrollYProgress, [0, 0.1], [0, -90])
    const contentOpacity = useTransform(scrollYProgress, [0, 0.07], [1, 0])
    const echoY = useTransform(scrollYProgress, [0, 0.35], ['0%', '-22%'])
    const echoRotate = useTransform(scrollYProgress, [0, 0.35], [0, -10])
    const echoX = useTransform(springX, [-1, 1], [-28, 28])
    const echoShift = useTransform(springY, [-1, 1], [-18, 18])
    const stageOpacity = useTransform(scrollYProgress, [0.32, 0.37], [0, 1])

    const fade = reduced ? undefined : { y: contentY, opacity: contentOpacity }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType !== 'mouse') return

        pointerX.set((event.clientX / window.innerWidth - 0.5) * 2)
        pointerY.set((event.clientY / window.innerHeight - 0.5) * 2)
    }

    return (
        <section
            ref={sectionRef}
            id='hero'
            data-tone='night'
            aria-labelledby='hero-titulli'
            className={cn('relative', reduced ? 'h-svh' : 'h-[320svh]')}
        >
            <div className='sticky top-0 h-svh overflow-hidden' onPointerMove={onPointerMove}>
                <motion.div
                    aria-hidden='true'
                    style={reduced ? undefined : { y: echoY, rotate: echoRotate }}
                    className='pointer-events-none absolute -right-[30vw] top-[10svh] h-[80svh] md:-right-[12vw] md:top-[6svh] md:h-[88svh]'
                >
                    <motion.div style={{ x: echoX, y: echoShift }} className='h-full'>
                        <Chevron className='jap-fade-in h-full w-auto -scale-x-100 text-jap-violet/30' />
                    </motion.div>
                </motion.div>

                <div className='relative z-10 flex h-full flex-col px-5 pb-7 pt-24 md:px-8 md:pb-10 md:pt-28 lg:px-12'>
                    <motion.div style={fade} className='grid grid-cols-12 gap-x-5'>
                        <p className='jap-rise col-span-12 flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-tone-muted md:col-span-7'>
                            <span className='font-heading tabular-nums text-tone-accent'>01</span>
                            <span aria-hidden='true' className='hidden h-px w-8 bg-tone-line sm:block' />
                            <span>{hero.eyebrow}</span>
                        </p>
                    </motion.div>

                    <div className='grid flex-1 grid-cols-12 content-center items-end gap-x-5 gap-y-7 py-6 md:gap-y-10'>
                        <div className='relative z-20 col-span-12 lg:col-span-8'>
                            <h1 id='hero-titulli' className='sr-only'>
                                JAP, grupi i vullnetarëve përmirësues të shoqërisë
                            </h1>

                            <svg
                                ref={logoRef}
                                viewBox={LOGO_VIEWBOX}
                                aria-hidden='true'
                                focusable='false'
                                className='block h-auto w-full overflow-visible'
                            >
                                <motion.g style={reduced ? undefined : { x: spreadLeft, opacity: sideOpacity }}>
                                    <g className='jap-rise [--rise-from:70px]' style={{ animationDelay: '0.1s' }}>
                                        <path d={LOGO_J} fill={GOLD} />
                                    </g>
                                </motion.g>

                                <g className='jap-rise [--rise-from:70px]' style={{ animationDelay: '0.22s' }}>
                                    <motion.path
                                        d={LOGO_CHEVRON}
                                        style={{
                                            scale: chevronScale,
                                            fill: reduced ? GOLD : chevronFill,
                                            originX: 0.576,
                                            originY: 0.268,
                                            transformBox: 'fill-box'
                                        }}
                                    />
                                </g>

                                <motion.g style={reduced ? undefined : { x: spreadRight, opacity: sideOpacity }}>
                                    <g className='jap-rise [--rise-from:70px]' style={{ animationDelay: '0.34s' }}>
                                        <path d={LOGO_P} fill={GOLD} fillRule='evenodd' />
                                    </g>
                                </motion.g>
                            </svg>
                        </div>

                        <motion.div style={fade} className='col-span-12 lg:col-span-4 lg:pb-[0.6vw]'>
                            <dl className='jap-rise max-w-sm' style={{ animationDelay: '0.5s' }}>
                                <dt className='flex items-baseline gap-3'>
                                    <span className='font-heading text-3xl font-bold tracking-tight'>
                                        {hero.entry}
                                    </span>

                                    <span className='font-serif text-xl italic text-tone-accent'>
                                        {hero.pronunciation}
                                    </span>
                                </dt>

                                <dd className='mt-1 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-tone-muted'>
                                    {hero.grammar}
                                </dd>

                                {hero.definitions.map((definition, index) => (
                                    <dd
                                        key={definition}
                                        className={cn(
                                            'mt-3 flex gap-3 text-[0.95rem] leading-relaxed text-tone-fg/85',
                                            index > 0 && '[@media(max-height:740px)]:hidden'
                                        )}
                                    >
                                        <span className='pt-0.5 font-heading text-xs tabular-nums text-tone-accent'>
                                            {index + 1}
                                        </span>

                                        <span>{definition}</span>
                                    </dd>
                                ))}
                            </dl>
                        </motion.div>
                    </div>

                    <motion.div style={fade} className='grid grid-cols-12 items-end gap-x-5 gap-y-6'>
                        <figure className='jap-rise col-span-12 md:col-span-7' style={{ animationDelay: '0.62s' }}>
                            <blockquote className='font-serif text-[1.75rem] italic leading-[1.05] sm:text-4xl lg:text-[3.1vw]'>
                                <p>“{hero.motto}”</p>
                            </blockquote>

                            <figcaption className='mt-3 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-tone-muted'>
                                {hero.attribution}
                            </figcaption>
                        </figure>

                        <div
                            className='jap-rise col-span-12 flex flex-wrap items-center gap-x-7 gap-y-4 md:col-span-5 md:justify-end'
                            style={{ animationDelay: '0.74s' }}
                        >
                            <a
                                href='#bashkohu'
                                className='group inline-flex items-center gap-3 bg-tone-accent px-6 py-4 text-sm font-semibold text-tone-accent-fg'
                            >
                                <RollText text={hero.primaryCta} />
                                <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-45' />
                            </a>

                            <a href='#projektet' className='group inline-flex items-center gap-2 text-sm font-semibold'>
                                <RollText text={hero.secondaryCta} />
                                <ArrowDown className='size-4 transition-transform duration-500 ease-out-expo group-hover:translate-y-0.5' />
                            </a>

                            <span aria-hidden='true' className='hidden items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-tone-muted xl:flex'>
                                <span className='relative block h-10 w-px overflow-hidden bg-tone-line'>
                                    <span className='jap-scroll-cue absolute inset-0 bg-tone-accent' />
                                </span>
                                {hero.scrollCue}
                            </span>
                        </div>
                    </motion.div>
                </div>

                {!reduced && (
                    <motion.div
                        style={{ opacity: stageOpacity }}
                        className='pointer-events-none absolute inset-0 z-30 flex flex-col justify-center gap-[2svh] px-5 md:px-8 lg:px-12'
                    >
                        <h2 className='absolute left-5 top-24 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-jap-paper/85 md:left-8 md:top-28 lg:left-12'>
                            <span className='font-heading tabular-nums text-jap-gold'>02</span>
                            <span aria-hidden='true' className='h-px w-8 bg-jap-paper/30' />
                            Manifesti
                        </h2>

                        {conjugation.map((item, index) => (
                            <ConjugationLine
                                key={item.id}
                                item={item}
                                index={index}
                                progress={scrollYProgress}
                                range={LINE_RANGES[index]}
                            />
                        ))}
                    </motion.div>
                )}
            </div>

            {!reduced && (
                <>
                    <div id='manifesti' aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-[99svh] h-px' />

                    <div
                        data-tone='blue'
                        aria-hidden='true'
                        className='pointer-events-none absolute inset-x-0 bottom-0 top-[125svh]'
                    />
                </>
            )}
        </section>
    )
}
